import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapPin, Navigation, AlertCircle } from 'lucide-react';
import { MAPBOX_TOKEN, DEFAULT_STYLE, ROUTE_CONFIG, MARKER_COLORS } from '@/lib/mapbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';

interface TrackingEvent {
  latitude: number;
  longitude: number;
  event_type: string;
  event_time: string;
  notes?: string;
}

interface MapViewProps {
  pickupAddress: string;
  deliveryAddress: string;
  pickupLat?: number;
  pickupLng?: number;
  deliveryLat?: number;
  deliveryLng?: number;
  trackingEvents?: TrackingEvent[];
  showRoute?: boolean;
}

export const MapView = ({
  pickupAddress,
  deliveryAddress,
  pickupLat,
  pickupLng,
  deliveryLat,
  deliveryLng,
  trackingEvents = [],
  showRoute = true,
}: MapViewProps) => {
  const { t } = useTranslation();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [routeInfo, setRouteInfo] = useState<{ distance: number; duration: number } | null>(null);

  useEffect(() => {
    // Check if token is configured
    if (!MAPBOX_TOKEN) {
      setError('Mapbox token is not configured');
      setIsLoading(false);
      return;
    }

    // Check if coordinates are available
    if (!pickupLat || !pickupLng || !deliveryLat || !deliveryLng) {
      setError('GPS coordinates are not available for this order');
      setIsLoading(false);
      return;
    }

    // Check if container exists
    if (!mapContainer.current) return;

    // Initialize map
    mapboxgl.accessToken = MAPBOX_TOKEN;

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: DEFAULT_STYLE,
        center: [pickupLng, pickupLat],
        zoom: 12,
      });

      // Add navigation controls
      map.current.addControl(
        new mapboxgl.NavigationControl({
          visualizePitch: false,
        }),
        'top-right'
      );

      map.current.on('load', async () => {
        if (!map.current) return;

        // Add pickup marker
        const pickupEl = document.createElement('div');
        pickupEl.className = 'w-10 h-10 flex items-center justify-center';
        pickupEl.innerHTML = `
          <div class="w-full h-full rounded-full bg-[${MARKER_COLORS.pickup}] flex items-center justify-center shadow-lg">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
            </svg>
          </div>
        `;

        new mapboxgl.Marker({ element: pickupEl })
          .setLngLat([pickupLng, pickupLat])
          .setPopup(
            new mapboxgl.Popup({ offset: 25 }).setHTML(
              `<div class="p-2">
                <p class="font-semibold text-sm">${t('orders.pickup')}</p>
                <p class="text-xs text-[#5A6B7A]">${pickupAddress}</p>
              </div>`
            )
          )
          .addTo(map.current);

        // Add delivery marker
        const deliveryEl = document.createElement('div');
        deliveryEl.className = 'w-10 h-10 flex items-center justify-center';
        deliveryEl.innerHTML = `
          <div class="w-full h-full rounded-full bg-[${MARKER_COLORS.delivery}] flex items-center justify-center shadow-lg">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
            </svg>
          </div>
        `;

        new mapboxgl.Marker({ element: deliveryEl })
          .setLngLat([deliveryLng, deliveryLat])
          .setPopup(
            new mapboxgl.Popup({ offset: 25 }).setHTML(
              `<div class="p-2">
                <p class="font-semibold text-sm">${t('orders.delivery')}</p>
                <p class="text-xs text-[#5A6B7A]">${deliveryAddress}</p>
              </div>`
            )
          )
          .addTo(map.current);

        // Add tracking event markers
        trackingEvents.forEach((event, index) => {
          if (!map.current || !event.latitude || !event.longitude) return;

          const eventEl = document.createElement('div');
          eventEl.className = 'w-8 h-8 flex items-center justify-center';
          eventEl.innerHTML = `
            <div class="w-full h-full rounded-full bg-[${MARKER_COLORS.tracking}] flex items-center justify-center shadow-lg border-2 border-white">
              <span class="text-white text-xs font-bold">${index + 1}</span>
            </div>
          `;

          new mapboxgl.Marker({ element: eventEl })
            .setLngLat([event.longitude, event.latitude])
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(
                `<div class="p-2">
                  <p class="font-semibold text-sm">${t(`tracking.eventTypes.${event.event_type}`)}</p>
                  <p class="text-xs text-[#5A6B7A]">${new Date(event.event_time).toLocaleString()}</p>
                  ${event.notes ? `<p class="text-xs mt-1">${event.notes}</p>` : ''}
                </div>`
              )
            )
            .addTo(map.current);
        });

        // Fetch and display route if enabled
        if (showRoute) {
          try {
            const routeUrl = `https://api.mapbox.com/directions/v5/mapbox/driving/${pickupLng},${pickupLat};${deliveryLng},${deliveryLat}?geometries=geojson&access_token=${MAPBOX_TOKEN}`;
            const response = await fetch(routeUrl);
            const data = await response.json();

            if (data.routes && data.routes.length > 0) {
              const route = data.routes[0];

              // Add route line
              map.current?.addSource('route', {
                type: 'geojson',
                data: {
                  type: 'Feature',
                  properties: {},
                  geometry: route.geometry,
                },
              });

              map.current?.addLayer({
                id: 'route',
                type: 'line',
                source: 'route',
                layout: {
                  'line-join': 'round',
                  'line-cap': 'round',
                },
                paint: {
                  'line-color': ROUTE_CONFIG.color,
                  'line-width': ROUTE_CONFIG.width,
                  'line-opacity': ROUTE_CONFIG.opacity,
                },
              });

              // Set route info
              setRouteInfo({
                distance: route.distance / 1000, // Convert to km
                duration: route.duration / 60, // Convert to minutes
              });
            }
          } catch (error) {
            console.error('Failed to fetch route:', error);
          }
        }

        // Fit bounds to show all markers
        const bounds = new mapboxgl.LngLatBounds();
        bounds.extend([pickupLng, pickupLat]);
        bounds.extend([deliveryLng, deliveryLat]);

        trackingEvents.forEach((event) => {
          if (event.latitude && event.longitude) {
            bounds.extend([event.longitude, event.latitude]);
          }
        });

        map.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15,
        });

        setIsLoading(false);
      });

      map.current.on('error', (e) => {
        console.error('Map error:', e);
        setError('Failed to load map');
        setIsLoading(false);
      });
    } catch (error) {
      console.error('Map initialization error:', error);
      setError('Failed to initialize map');
      setIsLoading(false);
    }

    // Cleanup
    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, [pickupLat, pickupLng, deliveryLat, deliveryLng, trackingEvents, showRoute, pickupAddress, deliveryAddress, t]);

  // Show error state
  if (error) {
    return (
      <div className="bg-[#EBF4F8] rounded-xl p-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error === 'GPS coordinates are not available for this order' ? (
              <div className="space-y-2">
                <p className="font-medium">{t('tracking.noCoordinates')}</p>
                <div className="flex items-center gap-3 text-[#5A6B7A] mt-4">
                  <MapPin size={20} className="text-[#0880AE]" />
                  <div className="text-sm">
                    <p className="font-medium text-[#2D3748]">{pickupAddress}</p>
                    <Navigation size={16} className="inline mx-2 text-[#5A6B7A]" />
                    <p className="font-medium text-[#2D3748]">{deliveryAddress}</p>
                  </div>
                </div>
              </div>
            ) : (
              error
            )}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 z-10 bg-[#EBF4F8] rounded-xl">
          <Skeleton className="w-full h-full rounded-xl" />
        </div>
      )}
      
      <div
        ref={mapContainer}
        className="w-full h-[400px] md:h-[500px] rounded-xl shadow-lg"
      />

      {routeInfo && !isLoading && (
        <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-3 z-10">
          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-1">
              <Navigation size={16} className="text-[#0880AE]" />
              <span className="font-medium text-[#2D3748]">
                {routeInfo.distance.toFixed(1)} km
              </span>
            </div>
            <div className="w-px h-4 bg-[#DBE2EA]" />
            <span className="text-[#5A6B7A]">
              {Math.round(routeInfo.duration)} min
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
